import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import TopBar from "@/components/TopBar";
import { Trash2, Minus, Plus, ShoppingBag, ArrowLeft, Truck, PackageCheck } from "lucide-react";

export default function Cart() {
  const cart = useCart();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[var(--background)]">
      <TopBar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-black" style={{fontFamily:'Cairo'}}>سلة الطلبات</h1>
          {cart.items.length > 0 && (
            <button onClick={cart.clear} className="text-sm text-red-500 flex items-center gap-1 hover:underline" data-testid="clear-cart">
              <Trash2 size={14}/> إفراغ السلة
            </button>
          )}
        </div>

        {cart.items.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center talabya-shadow-soft" data-testid="cart-empty">
            <div className="w-20 h-20 rounded-full bg-[var(--surface-alt)] flex items-center justify-center mx-auto mb-4">
              <ShoppingBag size={32} className="text-[var(--text-secondary)]"/>
            </div>
            <h2 className="text-xl font-bold mb-2" style={{fontFamily:'Cairo'}}>سلتك فارغة</h2>
            <p className="text-[var(--text-secondary)] mb-5">أضف بعض الوجبات الشهية للبدء</p>
            <button onClick={() => nav("/")} className="px-6 py-3 rounded-xl bg-primary-brand text-white font-bold talabya-shadow-float" data-testid="empty-browse">
              تصفح المنيو
            </button>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-3 mb-6">
              {cart.items.map((it, i) => (
                <div key={i} className="bg-white rounded-2xl p-4 talabya-shadow-soft flex gap-4" data-testid={`cart-line-${i}`}>
                  {it.image
                    ? <img src={it.image} alt={it.name} className="w-20 h-20 rounded-xl object-cover"/>
                    : <div className="w-20 h-20 rounded-xl bg-[var(--surface-alt)] flex items-center justify-center text-2xl">🍽</div>}
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <h3 className="font-bold" style={{fontFamily:'Cairo'}}>{it.name}</h3>
                      <button onClick={() => cart.removeAt(i)} className="text-red-400 hover:text-red-600" data-testid={`remove-${i}`}><Trash2 size={16}/></button>
                    </div>
                    {it.extras.length > 0 && (
                      <div className="text-xs text-[var(--text-secondary)] mt-1">
                        + {it.extras.map((e) => e.name).join("، ")}
                      </div>
                    )}
                    {it.notes && <div className="text-xs text-[var(--text-secondary)] italic mt-1">"{it.notes}"</div>}
                    <div className="flex justify-between items-center mt-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => cart.updateQty(i, it.quantity - 1)} className="w-7 h-7 rounded-full bg-[var(--surface-alt)] flex items-center justify-center" data-testid={`dec-${i}`}><Minus size={12}/></button>
                        <span className="font-bold w-6 text-center">{it.quantity}</span>
                        <button onClick={() => cart.updateQty(i, it.quantity + 1)} className="w-7 h-7 rounded-full bg-primary-brand text-white flex items-center justify-center" data-testid={`inc-${i}`}><Plus size={12}/></button>
                      </div>
                      <div className="font-black text-primary-brand" style={{fontFamily:'Cairo'}}>{it.line_total.toFixed(2)} د.ل</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Fulfillment */}
            <div className="bg-white rounded-2xl p-4 talabya-shadow-soft mb-4">
              <div className="text-sm font-bold mb-2">طريقة الاستلام</div>
              <div className="flex gap-2 bg-[var(--surface-alt)] p-1 rounded-xl">
                <button onClick={() => cart.setFulfillment("delivery")}
                  className={`flex-1 py-2 rounded-lg text-sm font-bold flex items-center justify-center gap-1.5 ${cart.fulfillment === "delivery" ? "bg-white talabya-shadow-soft text-primary-brand" : "text-[var(--text-secondary)]"}`}
                  data-testid="cart-ff-delivery">
                  <Truck size={14}/> توصيل
                </button>
                <button onClick={() => cart.setFulfillment("pickup")}
                  className={`flex-1 py-2 rounded-lg text-sm font-bold flex items-center justify-center gap-1.5 ${cart.fulfillment === "pickup" ? "bg-white talabya-shadow-soft text-primary-brand" : "text-[var(--text-secondary)]"}`}
                  data-testid="cart-ff-pickup">
                  <PackageCheck size={14}/> استلام
                </button>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-white rounded-2xl p-5 talabya-shadow-soft">
              <div className="flex justify-between py-1.5"><span className="text-[var(--text-secondary)]">المجموع الفرعي</span><span className="font-bold" data-testid="sum-subtotal">{cart.subtotal.toFixed(2)} د.ل</span></div>
              <div className="flex justify-between py-1.5"><span className="text-[var(--text-secondary)]">رسوم التوصيل</span><span className="font-bold" data-testid="sum-fee">{cart.delivery_fee.toFixed(2)} د.ل</span></div>
              <div className="border-t border-[var(--border)] my-2"/>
              <div className="flex justify-between py-1.5"><span className="font-bold">الإجمالي</span><span className="font-black text-xl text-primary-brand" style={{fontFamily:'Cairo'}} data-testid="sum-total">{cart.total.toFixed(2)} د.ل</span></div>

              <button onClick={() => nav("/checkout")} className="w-full mt-4 py-4 rounded-2xl bg-primary-brand text-white font-bold talabya-shadow-float flex items-center justify-center gap-2 hover:opacity-95" data-testid="go-checkout">
                متابعة إلى الدفع <ArrowLeft size={18}/>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
